import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { campaignsApi } from '../api/campaigns';
import './CampaignsPage.css';

const STATUS_CONFIG = {
  draft:      { label: 'Draft',      color: '#6B7280' },
  scheduled:  { label: 'Scheduled',  color: '#FFB800' },
  send:       { label: 'Send',       color: '#00D9FF' },
  sent:       { label: 'Sent',       color: '#39FF14' },
  paused:     { label: 'Paused',     color: '#A855F7' },
  cancelled:  { label: 'Cancelled',  color: '#374151' },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } },
};


function openRate(stats) {
  if (!stats?.sent) return 0;
  return Math.round((stats.opened / stats.sent) * 100);
}

function clickRate(stats) {
  if (!stats?.sent) return 0;
  return Math.round((stats.clicked / stats.sent) * 100);
}

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await campaignsApi.getAll();
        setCampaigns(data.campaigns || []);
      } catch (err) {
        console.error('[Campaigns]', err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="campaigns-loading">
        <div className="campaigns-spinner" />
      </div>
    );
  }

  return (
    <div className="campaigns-page">
      <div className="campaigns-inner">
        <div className="campaigns-header">
          <div>
            <h1 className="campaigns-title">Campaigns</h1>
            <p className="campaigns-sub">{campaigns.length} campaigns total</p>
          </div>
          <button className="new-campaign-btn" onClick={() => navigate('/campaigns/new')}>
            + New Campaign
          </button>
        </div>

        {campaigns.length === 0 ? (
          <div className="campaigns-empty">
            <p>No campaigns yet</p>
            <span>Create your first campaign to get started</span>
          </div>
        ) : (
          <motion.div
            className="campaigns-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {campaigns.map(campaign => {
              const statusCfg = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
              const or = openRate(campaign.stats);
              const cr = clickRate(campaign.stats);
              return (
                <motion.div
                  key={campaign._id}
                  className="campaign-card"
                  variants={cardVariants}
                  whileHover={{ y: -4, boxShadow: `0 8px 30px rgba(255,20,147,0.15)` }}
                >
                  <div className="campaign-card-top">
                    <div className="campaign-name-wrap">
                      <h3 className="campaign-name">{campaign.name}</h3>
                      {campaign.subject && (
                        <p className="campaign-subject">{campaign.subject}</p>
                      )}
                    </div>
                    <span
                      className="campaign-status-badge"
                      style={{
                        background: `${statusCfg.color}22`,
                        color: statusCfg.color,
                        border: `1px solid ${statusCfg.color}44`,
                      }}
                    >
                      {statusCfg.label}
                    </span>
                  </div>

                  <div className="campaign-stats">
                    <div className="campaign-stat">
                      <span className="cstat-value">{campaign.stats?.total || 0}</span>
                      <span className="cstat-label">Recipients</span>
                    </div>
                    <div className="campaign-stat">
                      <span className="cstat-value">{campaign.stats?.sent || 0}</span>
                      <span className="cstat-label">Sent</span>
                    </div>
                    <div className="campaign-stat">
                      <span className="cstat-value" style={{ color: or > 0 ? '#A855F7' : undefined }}>
                        {or}%
                      </span>
                      <span className="cstat-label">Open Rate</span>
                    </div>
                    <div className="campaign-stat">
                      <span className="cstat-value" style={{ color: cr > 0 ? '#39FF14' : undefined }}>
                        {cr}%
                      </span>
                      <span className="cstat-label">Click Rate</span>
                    </div>
                  </div>

                  {/* Open rate progress bar */}
                  {campaign.stats?.sent > 0 && (
                    <div className="campaign-bars">
                      <div className="bar-row">
                        <span className="bar-label">Opens</span>
                        <div className="bar-track">
                          <div className="bar-fill" style={{ width: `${or}%`, background: '#A855F7' }} />
                        </div>
                        <span className="bar-pct">{or}%</span>
                      </div>
                      <div className="bar-row">
                        <span className="bar-label">Clicks</span>
                        <div className="bar-track">
                          <div className="bar-fill" style={{ width: `${cr}%`, background: '#39FF14' }} />
                        </div>
                        <span className="bar-pct">{cr}%</span>
                      </div>
                    </div>
                  )}

                  <div className="campaign-meta">
                    {campaign.sentAt && (
                      <span className="campaign-date">
                        Sent {new Date(campaign.sentAt).toLocaleDateString()}
                      </span>
                    )}
                    {campaign.scheduledAt && !campaign.sentAt && (
                      <span className="campaign-date">
                        Scheduled {new Date(campaign.scheduledAt).toLocaleDateString()}
                      </span>
                    )}
                    {!campaign.sentAt && !campaign.scheduledAt && (
                      <span className="campaign-date">
                        Created {new Date(campaign.createdAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

      </div>
    </div>
  );
}
